import {
  css, keyframes,
} from '@emotion/react';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, type ReactNode, useRef,
} from 'react';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import markerImage from '../../../../assets/images/backgroundImages/marker.svg';

export const HERO_LAYOUT_TWO_COLUMN_BREAKPOINT = 1000;

const pageStyle = (theme: Theme) => css({
  position: 'relative',
  color: theme.textColors.primary,
  background: 'linear-gradient(90deg, #0039A9, #002A7A)',
  [`@media (min-width: ${HERO_LAYOUT_TWO_COLUMN_BREAKPOINT}px)`]: {
    background: 'linear-gradient(90deg, #0039A9, #002A7A, #001B4E)',
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle at top left, rgba(255, 255, 255, .1), rgba(255, 255, 255, 0))',
  },
});

const contentWrapperStyle = css({
  display: 'flex',
  margin: '0 auto',
  maxWidth: 1800,
  minHeight: '100dvh', // not supported by Firefox at the time. When using vh, the browser was showing scrollbar as the viewport height was oddly calculated
  overflow: 'hidden',
  [`@media (min-width: ${HERO_LAYOUT_TWO_COLUMN_BREAKPOINT}px)`]: {
    minHeight: '100vh',
  },
});

const imageColumnStyle = css({
  display: 'none',
  [`@media (min-width: ${HERO_LAYOUT_TWO_COLUMN_BREAKPOINT}px)`]: {
    position: 'relative',
    padding: 10,
    display: 'flex',
    justifyContent: 'flex-end',
    width: '45%',
    alignSelf: 'center',
  },
});

const imageWrapperStyle = css({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 700,
  paddingBottom: '122%',
});

const pulseAnimation = keyframes({
  '0%, 100%': {
    transform: 'scale(1)',
    opacity: .4,
  },
  '50%': {
    transform: 'scale(1.5)',
    opacity: .8,
  },
});

const drawLine1Animation = keyframes({
  '0%, 100%': {
    opacity: 0,
    width: 0,
  },
  '50%': {
    opacity: 1,
    width: 200,
  },
  '90%': {
    opacity: 0,
  },
});

const drawLine2Animation = keyframes({
  '0%, 100%': {
    opacity: 0,
    width: 0,
  },
  '50%': {
    opacity: 1,
    width: 150,
  },
  '90%': {
    opacity: 0,
  },
});

const drawLine3Animation = keyframes({
  '0%, 100%': {
    opacity: 0,
    translate: '0 0',
    width: 0,
  },
  '50%': {
    opacity: 1,
    translate: '-120px -40px',
    width: 180,
  },
  '90%': {
    opacity: 0,
  },
});

const floatAnimation = keyframes({
  '0%, 100%': {
    transform: 'translateY(0) scale(1)',
    opacity: .7,
  },
  '25%': {
    transform: 'translateY(-20px) scale(1.15)',
    opacity: 1,
  },
  '50%': {
    transform: 'translateY(5px) scale(.95)',
    opacity: .8,
  },
  '75%': {
    transform: 'translateY(-10px) scale(1.05)',
    opacity: .9,
  },
});

const globeRotateAnimation = keyframes({
  '0%': {
    transform: 'rotateY(0deg) rotateZ(5deg)',
  },
  '100%': {
    transform: 'rotateY(360deg) rotateZ(5deg)',
  },
});

const dotStyle = ({ offsetX, offsetY, animationDelay }: { offsetX: number; offsetY: number; animationDelay: string }) => css({
  position: 'absolute',
  top: `${50 + offsetY}%`,
  left: `${50 + offsetX}%`,
  width: 12,
  height: 12,
  background: 'rgba(255, 255, 255, .5)',
  borderRadius: '50%',
  opacity: .4,
  animation: `${pulseAnimation} 2s infinite ease-in-out`,
  animationDelay,
  boxShadow: '0 0 10px rgba(255, 255, 255, .3)',
});

type LineStyleParams = {
  offsetX: number;
  offsetY: number;
  animation: string;
  animationDelay: string;
  maxWidth: number;
  rotation: number;
};

const lineStyle = ({ offsetX, offsetY, animation, animationDelay, maxWidth, rotation }: LineStyleParams) => css({
  position: 'absolute',
  height: 1,
  maxWidth,
  top: `${50 + offsetY}%`,
  left: `${50 + offsetX}%`,
  transform: `rotate(${rotation}deg)`,
  animation: `${animation} 15s infinite ease-in-out`,
  animationDelay,
  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, .3), transparent)',
  transformOrigin: 'left center',
});

const markerStyle = ({ offsetX, offsetY, animationDelay }: { offsetX: number; offsetY: number; animationDelay: string }) => css({
  position: 'absolute',
  top: `${50 + offsetY}%`,
  left: `${50 + offsetX}%`,
  width: 50,
  height: 60,
  animation: `${floatAnimation} 10s infinite ease-in-out`,
  animationDelay,
  opacity: .7,
});

const globeWrapperStyle = css({
  position: 'absolute',
  top: '58%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '30vw',
  height: '30vw',
  maxWidth: 450,
  maxHeight: 450,
  opacity: .2,
});

const globeStyle = css({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, .3) 0%, rgba(255, 255, 255, .05) 100%)',
  border: '2px solid rgba(255, 255, 255, .2)',
  position: 'relative',
  animation: `${globeRotateAnimation} 30s linear infinite`,
  boxShadow: '0 0 50px rgba(255, 255, 255, .2)',

  '&:before': {
    content: '""',
    width: '100%',
    height: '50%',
    top: '25%',
    left: 0,
    position: 'absolute',
    borderRadius: '50%',
    border: '1px solid rgba(255, 255, 255, .2)',
  },
  '&:after': {
    content: '""',
    width: '50%',
    height: '100%',
    top: 0,
    left: '25%',
    position: 'absolute',
    borderRadius: '50%',
    border: '1px solid rgba(255, 255, 255, .2)',
  },
});

const equatorStyle = css({
  position: 'absolute',
  width: '100%',
  height: 1,
  top: '50%',
  background: 'rgba(255, 255, 255, .1)',
});

const meridianStyle = css({
  position: 'absolute',
  width: 1,
  height: '100%',
  left: '50%',
  background: 'rgba(255, 255, 255, .1)',
});

const linesStyle = [
  lineStyle({ offsetX: -48, offsetY: -27, animation: drawLine1Animation, animationDelay: '0s', maxWidth: 200, rotation: 25 }),
  lineStyle({ offsetX: 40, offsetY: 32, animation: drawLine3Animation, animationDelay: '3s', maxWidth: 150, rotation: -45 }),
  lineStyle({ offsetX: -28, offsetY: 33, animation: drawLine2Animation, animationDelay: '6s', maxWidth: 180, rotation: 60 }),
];

const dotsStyle = [
  dotStyle({ offsetX: -24, offsetY: -30, animationDelay: '0s' }),
  dotStyle({ offsetX: 17, offsetY: 0, animationDelay: '.5s' }),
  dotStyle({ offsetX: 5, offsetY: 13, animationDelay: '2s' }),
  dotStyle({ offsetX: -12, offsetY: 35, animationDelay: '1s' }),
  dotStyle({ offsetX: 35, offsetY: 37, animationDelay: '1.5s' }),
];

const markersStyle = [
  markerStyle({ offsetX: -42, offsetY: -51, animationDelay: '0s' }),
  markerStyle({ offsetX: 30, offsetY: -16, animationDelay: '1s' }),
  markerStyle({ offsetX: -17, offsetY: 0, animationDelay: '3s' }),
  markerStyle({ offsetX: 42, offsetY: 21, animationDelay: '2s' }),
  markerStyle({ offsetX: -50, offsetY: 37, animationDelay: '4s' }),
];

type AnimatedHeroLayoutProps = Readonly<{
  children?: ReactNode;
}>;

export const AnimatedHeroLayoutComponent: FC<AnimatedHeroLayoutProps> = (props) => {
  const containerRef = useRef<OverlayScrollbarsComponentRef | null>(null);

  return (
    <ScrollBarComponent
      css={pageStyle}
      type={ScrollbarType.Vertical}
      getScrollbarReference={ref => containerRef.current = ref}
    >
      <div css={contentWrapperStyle}>
        <div css={imageColumnStyle}>
          <div css={imageWrapperStyle}>
            {dotsStyle.map((style, index) => (
              <div key={index} css={style} />
            ))}

            {linesStyle.map((style, index) => (
              <div key={index} css={style} />
            ))}

            {markersStyle.map((style, index) => (
              <img
                css={style}
                key={index}
                src={markerImage}
                alt=""
              />
            ))}

            <div css={globeWrapperStyle}>
              <div css={globeStyle}>
                <div css={equatorStyle} />
                <div css={meridianStyle} />
              </div>
            </div>
          </div>
        </div>
        {props.children}
      </div>
    </ScrollBarComponent>
  );
};
